import { useState } from "react";
import { acceptInvitationAsync, rejectInvitationAsync } from "@features/invitations/services/invitationSvc";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from '@core/types/errorResponse';
import { usePendingInvitationsStore } from '@features/user/invitations/store/pendingInvitationsStore';

/**
 * Custom hook providing actions for accepting and rejecting user invitations.
 *
 * This hook manages the busy state during asynchronous invitation operations,
 * and handles success and error notifications.
 *
 * @returns An object containing:
 * - `busy`: A boolean indicating if an operation is in progress.
 * - `doAccept`: Function to accept an invitation by its ID.
 * - `doReject`: Function to reject an invitation by its ID.
 */
export function useMyInviteActions() {
    // State to track if an operation is in progress
    const [busy, setBusy] = useState(false);

    // Hook to handle API errors
    const showError = useApiError();

    /**
     * Handles the acceptance of an invitation by its ID.
     * 
     * Sets the busy state to true while the asynchronous operation is in progress.
     * Calls the `acceptInvitationAsync` function to accept the invitation.
     * On success, displays a success message using `showSuccess`.
     * On failure, displays an error message using `showError`.
     * Ensures the busy state is reset to false after the operation completes.
     * 
     * @param id - The unique identifier of the invitation to accept.
     * @returns A promise that resolves to the response of the accept operation.
     * @throws Rethrows any error encountered during the operation after displaying an error message.
     */
    const doAccept = async (id: number) => {
        // Set busy state to true while the operation is in progress
        setBusy(true);

        try {
            // Call the service to accept the invitation
            const response = await acceptInvitationAsync(id);

            // Handle success and error responses
            if (response.isSuccess()) {
                showSuccess(response.getData()?.message);
                await usePendingInvitationsStore.getState().invalidate();
            } else {
                showError(response.getError() as ErrorResponse);
            }
            return response;
        } catch (error) {
            showError(error as ErrorResponse);
            throw error;
        } finally {
            setBusy(false);
        }
    };

    /**
     * Asynchronously rejects an invitation by its ID.
     *
     * Sets the busy state to true while the operation is in progress.
     * Calls the `rejectInvitationAsync` function to perform the rejection.
     * On success, displays a success message using `showSuccess`.
     * On failure, displays an error message using `showError`.
     * Always resets the busy state to false after completion.
     *
     * @param id - The unique identifier of the invitation to reject.
     * @returns A promise that resolves to the response of the rejection operation.
     * @throws Rethrows any error encountered during the rejection process.
     */
    const doReject = async (id: number) => {
        // Indicate that the operation is in progress
        setBusy(true);
        try {
            // Call the service to reject the invitation
            const response = await rejectInvitationAsync(id);

            // Handle success and error responses
            if (response.isSuccess()) {
                showSuccess(response.getData()?.message);
                await usePendingInvitationsStore.getState().invalidate();
            } else {
                showError(response.getError() as ErrorResponse);
            }
            return response;
        } catch (error) {
            showError(error as ErrorResponse);
            throw error;
        } finally {
            setBusy(false);
        }
    };

    return { busy, doAccept, doReject };

}